import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AUTH_ERRORS, getAuthErrorMessage } from '@/constants/authErrors';

export function GoogleCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  // The OAuth state is single-use server-side (GetAndDelete): a second POST
  // with the same state is guaranteed to fail. Guard against effect re-runs
  // (StrictMode remounts, HMR) double-firing the exchange.
  const exchangeStarted = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      // All navigations replace the history entry: the callback URL must
      // never linger in history, or back-swipes / tab restores / URL
      // autocomplete re-visit it with an already-consumed state and hit
      // "Invalid or expired state parameter".
      // Handle OAuth error
      if (errorParam) {
        setError(AUTH_ERRORS.GOOGLE_OAUTH_CALLBACK_FAILED);
        setTimeout(() => navigate('/login', { replace: true }), 3000);
        return;
      }

      if (!code || !state) {
        setError(AUTH_ERRORS.OAUTH_STATE_INVALID);
        setTimeout(() => navigate('/login', { replace: true }), 3000);
        return;
      }

      if (exchangeStarted.current) return;
      exchangeStarted.current = true;

      try {
        // Exchange code for session token
        const response = await fetch('/api/auth/google/callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, state }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          // Use centralized error message or fallback to server error
          throw new Error(getAuthErrorMessage(errorText));
        }

        const data = await response.json();

        // Store session token
        apiClient.setSessionToken(data.session_token);

        // Redirect to home
        navigate('/home', { replace: true });
      } catch (err: any) {
        console.error('[Google Callback] Error:', err);
        // Re-visiting a stale callback URL while already signed in (tab
        // restore, history) is not an auth failure — return to the app
        // silently; the normal auth guard handles genuinely dead sessions.
        if (localStorage.getItem('sessionToken')) {
          navigate('/home', { replace: true });
          return;
        }
        setError(getAuthErrorMessage(err));
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:bg-background dark:from-transparent dark:via-transparent dark:to-transparent flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">Authentication Error</p>
                <p>{error}</p>
                <p className="text-sm text-muted-foreground">Redirecting to login...</p>
              </div>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-xl font-semibold text-foreground">Completing Google sign-in...</p>
            <p className="text-sm text-muted-foreground mt-2">Please wait while we verify your credentials</p>
          </div>
        )}
      </div>
    </div>
  );
}
